"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { AnimatePresence, motion, useMotionTemplate, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Mail, ArrowUp, Menu, Calendar } from "lucide-react";
import { OctarnalLogo } from "@/assets/icons/MainPageIcon";
import { useCalBooking } from "@/providers/booking/CalBookingProvider";
interface MobileActionBarProps {
  visible: boolean;
  onOpenMenu: () => void;
  onScrollTop: () => void;
}
const ICON = {
  size: 20,
  strokeWidth: 2
} as const;
const SLOTS = 5;
const CENTER = 2;

// The lit edge is a border, not a bloom: RIM_R is exactly RIM_WIDTH smaller
// than the bar's hole and the rim sits RIM_WIDTH higher, so the same 2px shows
// along the straight top and all the way around the notch.
const RIM_WIDTH = 2;
const BAR_NOTCH_R = 36;
const RIM_R = BAR_NOTCH_R - RIM_WIDTH;
const BUBBLE = 56;
const IDLE_RETURN_MS = 3000;
const MobileActionBar = ({
  visible,
  onOpenMenu,
  onScrollTop
}: MobileActionBarProps) => {
  const {
    enabled: calEnabled,
    goToBooking
  } = useCalBooking();
  const [active, setActive] = useState({
    index: CENTER,
    at: 0
  });
  const pathname = usePathname();
  const router = useRouter();
  const handleBooking = () => {
    if (calEnabled) {
      goToBooking();
    } else {
      window.location.href = "/contact";
    }
  };

  // The logo is the mark, so it goes where the mark goes: the top of the home
  // page. Off home that is a navigation, and ScrollProvider lands it on the
  // banner; on home there is nothing to load, so it is just the scroll.
  const goHome = () => {
    if (pathname === "/") {
      onScrollTop();
      return;
    }
    router.push("/");
  };
  const actions = [{
    key: "book",
    label: "Book a session",
    icon: <Calendar {...ICON} />,
    run: handleBooking
  }, {
    key: "mail",
    label: "Contact us",
    icon: <Mail {...ICON} />,
    run: () => {
      window.location.href = "/contact";
    }
  }, {
    key: "logo",
    label: "Octarnal home",
    icon: <OctarnalLogo size={22} fill="currentColor" />,
    run: goHome
  }, {
    key: "top",
    label: "Scroll to top",
    icon: <ArrowUp {...ICON} />,
    run: onScrollTop
  }, {
    key: "menu",
    label: "Open menu",
    icon: <Menu {...ICON} />,
    run: onOpenMenu
  }];
  const slot = useMotionValue(CENTER);
  const glide = useSpring(slot, {
    stiffness: 240,
    damping: 28,
    mass: 0.9
  });
  const bubbleX = useTransform(glide, v => `${v * 100}%`);
  const notch = useTransform(glide, v_0 => (v_0 + 0.5) * (100 / SLOTS));
  const barMask = useMotionTemplate`radial-gradient(circle ${BAR_NOTCH_R}px at ${notch}% 0px, transparent 98%, #000 100%)`;
  const rimMask = useMotionTemplate`radial-gradient(circle ${RIM_R}px at ${notch}% ${RIM_WIDTH}px, transparent 98%, #000 100%)`;
  useEffect(() => {
    slot.set(active.index);
  }, [active, slot]);
  useEffect(() => {
    if (active.index === CENTER) return;
    const timer = window.setTimeout(() => setActive({
      index: CENTER,
      at: Date.now()
    }), IDLE_RETURN_MS);
    return () => window.clearTimeout(timer);
  }, [active]);

  // Don't let the bar slide back on screen mid-journey.
  useEffect(() => {
    if (!visible) setActive({
      index: CENTER,
      at: 0
    });
  }, [visible]);
  const select = (index: number) => {
    setActive({
      index,
      at: Date.now()
    });
    actions[index].run();
  };
  return <motion.nav aria-label="Quick actions" inert={!visible} initial={{
    opacity: 0,
    y: 80
  }} animate={visible ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 80
  }} transition={{
    duration: 0.35,
    ease: "easeOut"
  }} className="fixed inset-x-0 bottom-0 z-50 md:hidden">
      <div className="relative" style={{
      height: "calc(4rem + env(safe-area-inset-bottom))",
      paddingBottom: "env(safe-area-inset-bottom)"
    }}>
        {/* Rim — the same shape with a hole 2px tighter, nudged up by 2px. What
            shows past the bar underneath is a border-2 of primary that follows
            the notch instead of stopping at it. */}
        <motion.div aria-hidden className="absolute inset-x-0 bottom-0 rounded-t-[30px] bg-primary" style={{
        top: -RIM_WIDTH,
        maskImage: rimMask,
        WebkitMaskImage: rimMask,
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat"
      }} />

        {/* The bar */}
        <motion.div aria-hidden className="absolute inset-0 rounded-t-[28px] bg-card-foreground" style={{
        maskImage: barMask,
        WebkitMaskImage: barMask,
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat"
      }} />

        {/* The five slots */}
        <div className="absolute inset-x-0 top-0 flex h-16">
          {actions.map((action, index_0) => <button key={action.key} type="button" aria-label={action.label} onClick={() => select(index_0)} className="
                flex
                flex-1
                cursor-pointer
                items-center
                justify-center
                text-white/70
                transition-opacity
                duration-300
              " style={{
          opacity: active.index === index_0 ? 0 : 1
        }}>
              {action.icon}
            </button>)}
        </div>

        {/* The bubble */}
        <motion.div className="pointer-events-none absolute left-0 top-0 flex w-1/5 justify-center" style={{
        x: bubbleX
      }}>
          <div className="relative flex items-center justify-center" style={{
          width: BUBBLE,
          height: BUBBLE,
          marginTop: -BUBBLE / 2
        }}>
            <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full bg-primary opacity-60 blur-lg" />
            <span aria-hidden className="aurora-halo pointer-events-none absolute -inset-5 rounded-full bg-primary/45 blur-2xl" />
            <span aria-hidden className="aurora-ring pointer-events-none absolute -inset-2 rounded-full opacity-70 blur-md" style={{
            backgroundImage: "conic-gradient(from 0deg, transparent 0%, var(--color-primary) 18%, transparent 42%, var(--color-primary) 66%, transparent 92%)"
          }} />

            <motion.button type="button" aria-hidden tabIndex={-1} onClick={() => select(active.index)} whileTap={{
            scale: 0.92
          }} className="
                pointer-events-auto
                relative
                flex
                size-14
                cursor-pointer
                items-center
                justify-center
                overflow-hidden
                rounded-full
                bg-primary
                shadow-[0_6px_20px_rgba(0,0,0,0.45)]
              ">
              <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-linear-to-b from-white/35 to-transparent" />

              <AnimatePresence mode="wait" initial={false}>
                <motion.span key={active.index} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3,
                ease: "linear"
              }} className="relative z-10 flex items-center justify-center text-card-foreground">
                  {active.index === CENTER ? <OctarnalLogo size={30} fill="currentColor" /> : actions[active.index].icon}
                </motion.span>
              </AnimatePresence>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.nav>;
};
export default MobileActionBar;